import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, Space, message, Pagination } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";
import AddUsages from "./AddUsages";
import ActionDropdown from "../../production/components/ActionDropdown";

const Usages = () => {
  const [usages, setUsages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false); // State for table loading

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default to 10 items per page

  const handleShow = (edit = false, usage = null) => {
    setEditMode(edit);
    setSelectedUsage(usage);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedUsage(null);
  };

  const fetchUsages = async () => {
    setLoadingTable(true); // Start loading
    try {
      const res = await axios.get(`${API_URL}/api/parametre/type/USAGE`, {
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      // Sort by parametreId in descending order to show latest first
      // If your API provides a 'createdAt' timestamp, use that for more accurate "latest" sorting.
      const sortedUsages = res.data.sort((a, b) => b.parametreId - a.parametreId);
      setUsages(sortedUsages);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des usages.");
    } finally {
      setLoadingTable(false); // End loading
    }
  };

  useEffect(() => {
    fetchUsages();
  }, [showModal]); // Re-fetch when modal closes (after add/edit)

  // --- Handlers for Add and Update to keep new/modified at top ---
  const handleAddSuccess = (newUsage) => {
    setUsages((prev) => [newUsage, ...prev]); // Add new at the top
    setCurrentPage(1); // Go to the first page to see the new item
  };

  const handleUpdateSuccess = (updatedUsage) => {
    setUsages((prev) => {
      const withoutOld = prev.filter(
        (usage) => usage.parametreId !== updatedUsage.parametreId
      );
      return [updatedUsage, ...withoutOld]; // Place updated at the top
    });
    // The current page usually doesn't need to change for an update,
    // as the item will simply move to the top of the list, which might be on the current page or page 1.
  };
  // --- End Handlers ---

  const handleDelete = (parametreId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet usage ?",
      icon: <ExclamationCircleOutlined />,
      content: "Cette action est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/api/parametre/${parametreId}`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
            },
          });
          setUsages((prev) =>
            prev.filter((usage) => usage.parametreId !== parametreId)
          );
          message.success("Usage supprimé avec succès.");

          // After deletion, check if the current page is now empty
          // or if the last item on the last page was deleted.
          const totalItemsAfterDeletion = usages.length - 1;
          const totalPagesAfterDeletion = Math.ceil(totalItemsAfterDeletion / pageSize);
          if (currentPage > totalPagesAfterDeletion && totalPagesAfterDeletion > 0) {
            setCurrentPage(totalPagesAfterDeletion);
          } else if (totalPagesAfterDeletion === 0) {
            setCurrentPage(1); // Reset to page 1 if all items are deleted
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression de l'usage.");
        }
      },
    });
  };

  // Pagination logic
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = usages.slice(startIndex, endIndex);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow()}
              >
                Ajouter de nouveaux usages
              </Button>
              <div className="card">
                <h5 className="card-header">Paramétrage des usages</h5>
                <div className="table-responsive text-nowrap">
                  {loadingTable ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : paginatedData.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Catégorie</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="table-border-bottom-0">
                        {paginatedData.map((usa) => (
                          <tr key={usa.parametreId}> {/* Changed sizetr to tr */}
                            <td>{usa.libelleParametre}</td>
                            <td>{usa?.categorie?.nom}</td>
                            <td>
                              <ActionDropdown
                              actions={[
                                {
                                  key: "edit",
                                  label: "Modifier",
                                  icon: <i className="bx bx-edit-alt me-1" />,
                                  onClick: () => handleShow(true, usa),
                                },
                                {
                                  key: "delete",
                                  label: "Supprimer",
                                  icon: <i className="bx bx-trash me-1" />,
                                  danger: true,
                                  onClick: () => handleDelete(usa.parametreId),
                                },
                              ]}
                            />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center p-3">Aucun usage trouvé.</p>
                  )}
                  {/* Pagination  ouside the table, clearly visible */}
                  {usages.length > pageSize && ( // Only show pagination if there's more than one page
                    <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={usages.length}
                        onChange={(page, size) => { // Allow changing page size
                          setCurrentPage(page);
                          setPageSize(size);
                        }}
                        showSizeChanger // Enable size changer
                        pageSizeOptions={['5', '10', '20', '50']} // Options for page size
                        showTotal={(total, range) => `${range[0]}-${range[1]} sur ${total} éléments`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddUsages
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedUsages={selectedUsage}
        onAddSuccess={handleAddSuccess}
        onUpdateSuccess={handleUpdateSuccess}
        existingUsages={usages} 
      />
    </div>
  );
};

export default Usages;